// KIHIRDETÉS / VISSZAVONÁS — a `product_updates.publish` jog művelete, SZÁNDÉKOSAN külön dialógusban.
//
// MIÉRT NEM EGY LEGÖRDÜLŐ A FORMBAN: a partnereknek szóló kihirdetés az a lépés, amit nem lehet „visszaírni"
// (a bejegyzés kikerült a portálra), ezért megerősítést kap, és a KÉT célt (csak belsősök vs. partnerek is)
// egymás mellett, kimondva kínálja — nem egy mező két értékeként.
//
// A VISSZAVONÁS ugyanitt: piszkozatba állít, és a partner-felületről azonnal eltűnik. A `publishedAt`
// szerver-oldalon MEGMARAD (történeti tény), ezért a dialógus ezt ki is mondja.
import type { ProductUpdateRow } from '@atlas/contracts';
import type { QueryKey } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Text } from '@/components/ui/text';
import { productUpdateKeys } from '@/features/product-updates/api';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';

export interface ProductUpdatePublishDialogProps {
  /** A cél-tétel (`null` → a dialógus zárva). */
  row: ProductUpdateRow | null;
  onClose: () => void;
  /** A nyitott lista cache-kulcsa (a művelet után ezt is invalidáljuk). */
  listKey: QueryKey;
}

export function ProductUpdatePublishDialog({
  row,
  onClose,
  listKey,
}: ProductUpdatePublishDialogProps): JSX.Element {
  const { t } = useTranslation();
  const isPublished = row?.status === 'published';

  const mutation = useEntityMutation<{ id: string; action: 'internal' | 'public' | 'revoke' }>({
    mutationFn: ({ id, action }) =>
      action === 'revoke'
        ? // A visszavonásnak nincs body-ja (`c.noBody()`), ezért csak a path-param megy.
          api.productUpdates.unpublish.mutation({ params: { id } })
        : api.productUpdates.publish.mutation({
            params: { id },
            body: { visibility: action },
          }),
    invalidateKeys: [listKey, productUpdateKeys.lists()],
    messages: {
      success: t('toast:productUpdates.published'),
      conflict: t('toast:productUpdates.conflict'),
      forbidden: t('toast:productUpdates.publishForbidden'),
      // A 422 itt egy KIMONDOTT üzleti szabály: publikus kihirdetéshez összefoglaló kell.
      validation: t('toast:productUpdates.publishNeedsSummary'),
      error: t('toast:productUpdates.error'),
    },
  });

  const run = (action: 'internal' | 'public' | 'revoke'): void => {
    if (!row) return;
    mutation.mutate({ id: row.id, action }, { onSettled: onClose });
  };

  return (
    <Dialog open={row !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('productUpdates.publishTitle')}</DialogTitle>
          <DialogDescription>{row?.title.hu}</DialogDescription>
        </DialogHeader>

        {/* A KÖZÖS `DialogBody` (`px-6 py-4`) — a `DialogContent` szándékosan `p-0`, a paddingot a szekciók
            hozzák. Egy sima `div` itt a panel széléig engedte a tartalmat. */}
        <DialogBody className="space-y-3">
          <Text tone="secondary">{t('productUpdates.publishHelp')}</Text>
          {isPublished && (
            <Text tone="tertiary">
              {row?.visibility === 'public'
                ? t('productUpdates.publishCurrentPublic')
                : t('productUpdates.publishCurrentInternal')}
            </Text>
          )}
        </DialogBody>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            {t('productUpdates.cancel')}
          </Button>
          {isPublished && (
            <Button variant="outline" onClick={() => run('revoke')} disabled={mutation.isPending}>
              {t('productUpdates.unpublish')}
            </Button>
          )}
          <Button variant="outline" onClick={() => run('internal')} disabled={mutation.isPending}>
            {t('productUpdates.publishInternal')}
          </Button>
          <Button variant="brand" onClick={() => run('public')} disabled={mutation.isPending}>
            {t('productUpdates.publishPublic')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
